//  plan-matching.js
//  Finds the real transaction a planned series (planned.js) was waiting for.
//
//  Shared by the web app (on every data change) and the server sync pipeline
//  (as transactions land), so both agree on what links.
//
//  A transaction is a CANDIDATE for a plan when it moves money the same way,
//  lands near one of the plan's expected dates, sits on the plan's account if
//  one was picked, and its amount is close. It is only linked AUTOMATICALLY
//  when there is also text evidence — the "shows up as" hint, or a word of the
//  plan's name in the descriptor — and the pairing is one-to-one. Amount and
//  date alone are weak: a $58 grocery run two days after a $60 gym plan's first
//  date fits both, and a wrong link would quietly fold groceries into the gym
//  series. Those become suggestions the user confirms instead.

import { activePlans, parseDay } from './planned.js';
import { expandRecurring } from './recurrence.js';
import { seriesKeyOf, normalizeDesc } from './series-key.js';
import { firebaseSafeKey } from '../firebaseKey.js';

// How far from an expected date a first charge may land, per cadence. Billing
// dates drift (weekends, processing lag, a start date that was approximate),
// and more so the longer the cycle.
const WINDOW_DAYS = { daily: 1, weekly: 3, biweekly: 5, monthly: 10, semiannual: 21, yearly: 30 };

// Amount alone must be close. With text evidence the band opens to the one
// matchExistingSeries uses — first charges are often prorated or carry a fee.
const AMOUNT_TOLERANCE = 0.25;
const EVIDENCED_LOW = 0.25;
const EVIDENCED_HIGH = 4;

// Suggestions shown per plan. More than a few means the plan is too vague to
// match, and a long list of guesses doesn't help the user pick.
const MAX_SUGGESTIONS = 3;

// Words in a plan name that say nothing about which merchant charged it.
const STOP_WORDS = new Set([
  'the', 'and', 'for', 'bill', 'payment', 'monthly', 'weekly', 'yearly', 'annual',
  'membership', 'subscription', 'plan', 'new', 'my', 'fee', 'service',
]);

const DAY_MS = 24 * 60 * 60 * 1000;
const words = (s) => normalizeDesc(s).split(/[^a-z0-9]+/).filter(Boolean);
const squash = (s) => normalizeDesc(s).replace(/[^a-z0-9]/g, '');

/** Does the descriptor name the plan — via its hint, or a word of its name? */
export function textEvidence(plan, tx) {
  const hint = squash(plan.matchHint);
  if (hint && squash(tx.description).includes(hint)) return true;
  const descWords = new Set(words(tx.description));
  return words(plan.name)
    .filter((w) => w.length >= 3 && !STOP_WORDS.has(w))
    .some((w) => descWords.has(w));
}

function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

/**
 * How well one transaction fits one plan, or null if it doesn't.
 * @returns {null | {evidence: boolean, offsetDays: number}}
 */
export function planCandidate(plan, tx, { today = new Date() } = {}) {
  if (!tx || tx.type !== plan.type) return null;
  if (plan.accountId && tx.accountId !== plan.accountId) return null;
  if (plan.dismissedTxIds && plan.dismissedTxIds[tx.id]) return null;

  const day = parseDay(tx.date);
  const first = parseDay(plan.firstDate);
  if (!day || !first) return null;

  // Nearest expected occurrence. A first date that passed unmatched may have
  // really started a cycle later, so every occurrence up to now is in play.
  const window = WINDOW_DAYS[plan.recurrenceType] ?? WINDOW_DAYS.monthly;
  const until = addDays(day > today ? day : parseDay(today), window);
  const expected = [first, ...expandRecurring({ lastDate: first, recurrenceType: plan.recurrenceType }, first, until)];
  const offsetDays = Math.min(...expected.map((e) => Math.abs(Math.round((day - e) / DAY_MS))));
  if (offsetDays > window) return null;

  const evidence = textEvidence(plan, tx);
  const ratio = Math.abs(Number(tx.amount) || 0) / Number(plan.amount);
  const amountOk = evidence
    ? ratio >= EVIDENCED_LOW && ratio <= EVIDENCED_HIGH
    : Math.abs(ratio - 1) <= AMOUNT_TOLERANCE;
  if (!amountOk) return null;

  return { evidence, offsetDays };
}

/**
 * Every plan ↔ transaction fit, split into what can link on its own and what
 * the user should be asked about.
 *
 * A transaction already belonging to an established series is off the table:
 * that series existed before the plan, so the plan can't be what started it.
 * The one exception is a series whose FIRST recurring instance is this very
 * transaction — the importer may have flagged a new charge recurring itself.
 *
 * @returns {{ links: Array<{plan, tx}>, suggestions: Array<{plan, tx, offsetDays}> }}
 */
export function matchPlans(transactions = [], planned = [], { today = new Date() } = {}) {
  const plans = activePlans(planned);
  if (plans.length === 0 || transactions.length === 0) return { links: [], suggestions: [] };

  const firstRecurring = {};
  transactions.forEach((t) => {
    if (!t || !t.isRecurring) return;
    const day = parseDay(t.date);
    if (!day) return;
    const key = seriesKeyOf(t);
    if (firstRecurring[key] === undefined || day.getTime() < firstRecurring[key]) firstRecurring[key] = day.getTime();
  });

  const eligible = transactions.filter((t) => {
    if (!t || !t.id || (t.type !== 'expense' && t.type !== 'income')) return false;
    const day = parseDay(t.date);
    if (!day) return false;
    const first = firstRecurring[seriesKeyOf(t)];
    return first === undefined || first >= day.getTime();
  });

  const pairs = [];
  plans.forEach((plan) => {
    eligible.forEach((tx) => {
      const fit = planCandidate(plan, tx, { today });
      if (fit) pairs.push({ plan, tx, ...fit });
    });
  });

  // One-to-one among the evidenced pairs: the plan has exactly one evidenced
  // candidate, and that transaction is evidenced for no other plan.
  const evidenced = pairs.filter((p) => p.evidence);
  const count = (list, pick) => list.reduce((m, p) => m.set(pick(p), (m.get(pick(p)) || 0) + 1), new Map());
  const perPlan = count(evidenced, (p) => p.plan.id);
  const perTx = count(evidenced, (p) => p.tx.id);
  const links = evidenced
    .filter((p) => perPlan.get(p.plan.id) === 1 && perTx.get(p.tx.id) === 1)
    .map(({ plan, tx }) => ({ plan, tx }));

  const linkedPlans = new Set(links.map((l) => l.plan.id));
  const linkedTxs = new Set(links.map((l) => l.tx.id));
  const shown = new Map();
  const suggestions = pairs
    .filter((p) => !linkedPlans.has(p.plan.id) && !linkedTxs.has(p.tx.id))
    // Evidence first, then nearest the expected date.
    .sort((a, b) => (b.evidence - a.evidence) || (a.offsetDays - b.offsetDays))
    .filter((p) => {
      const n = shown.get(p.plan.id) || 0;
      if (n >= MAX_SUGGESTIONS) return false;
      shown.set(p.plan.id, n + 1);
      return true;
    })
    .map(({ plan, tx, offsetDays }) => ({ plan, tx, offsetDays }));

  return { links, suggestions };
}

/**
 * The multi-location writes that link a plan to its first real charge: the
 * transaction becomes the first instance of a recurring series with the
 * plan's cadence, the series takes the plan's name, and the plan retires.
 *
 * The plan is kept (status 'linked') rather than deleted, so the link can be
 * traced — and so a second client racing this one writes the same values.
 */
export function planLinkWrites(uid, plan, tx, { seriesNames = {}, now = new Date() } = {}) {
  const base = `users/${uid}`;
  const key = seriesKeyOf(tx);
  const updates = {
    [`${base}/transactions/${tx.id}/isRecurring`]: true,
    [`${base}/transactions/${tx.id}/recurrenceType`]: plan.recurrenceType,
    [`${base}/plannedSeries/${plan.id}/status`]: 'linked',
    [`${base}/plannedSeries/${plan.id}/linkedKey`]: key,
    [`${base}/plannedSeries/${plan.id}/linkedTxId`]: tx.id,
    [`${base}/plannedSeries/${plan.id}/linkedAt`]: now.toISOString(),
  };
  // The user's own category choice on the charge wins; so does a real one
  // over the plan's default "Other".
  if (!tx.manualCategory && plan.category && (plan.category !== 'Other' || !tx.category)) {
    updates[`${base}/transactions/${tx.id}/category`] = plan.category;
  }
  const nameKey = firebaseSafeKey(key);
  if (plan.name && !seriesNames[nameKey]) {
    updates[`${base}/seriesNames/${nameKey}`] = plan.name;
  }
  return updates;
}
